import { type KeyboardEvent, useState } from "react";

import {
  Button,
  Icon,
  Input,
  Text,
  toastError,
  toastSuccess,
} from "@alpacahq/ui/old";

import {
  PRIMARY_WATCHLIST_NAME,
  useEnsurePrimaryWatchlist,
  useGetQuotes,
  useGetTradingAccount,
  useGetWatchlist,
  useGetWatchlists,
  useMarketDataFeed,
  useMutationRequest,
} from "~/v2/api/hooks";
import {
  type AddSymbolToWatchlistParams,
  addSymbolToWatchlist,
  type RemoveSymbolFromWatchlistParams,
  removeSymbolFromWatchlist,
  type Watchlist as WatchlistType,
} from "~/v2/api/rest/account";
import { Card } from "~/v2/components";
import { SymbolLink } from "~/v2/components/SymbolLink";
import { getPercentageChange } from "~/v2/utils";
import { cn } from "~/v2/utils/cn";
export const Watchlist = () => {
  // TODO (MLA): Watchlist is an account feature, it is even available to paper accounts.
  // However, until now we have treated it as a party feature. To avoid UX confusion, we'll
  // leave it tied to the trading account for now and revisit during the watchlist revamp.
  const { account } = useGetTradingAccount();

  const watchlistsQuery = useGetWatchlists({
    accountID: account?.id || "",
    options: { enabled: !!account?.id },
  });

  const { watchlist, refetch } = useGetWatchlist({
    accountID: account?.id || "",
    name: PRIMARY_WATCHLIST_NAME,
    options: {
      enabled: watchlistsQuery.watchlists.some(
        (w) => w.name === PRIMARY_WATCHLIST_NAME,
      ),
    },
  });

  useEnsurePrimaryWatchlist({
    accountID: account?.id || "",
    isFetched: watchlistsQuery.isFetched,
    watchlists: watchlistsQuery.watchlists,
  });

  const assets = watchlist?.assets;
  const watchlistAssets = Array.isArray(assets) ? assets : [];
  const watchlistSymbols = watchlistAssets.map(({ symbol }) => symbol);

  // Watchlist contains mixed symbols — use default isOvernightTradable=true so
  // overnight-eligible symbols get the correct feed during overnight sessions.
  const { latestFeed } = useMarketDataFeed();

  const { quotes } = useGetQuotes({
    symbols: watchlistSymbols,
    feed: latestFeed,
    options: {
      enabled: !!watchlistSymbols.length,
    },
  });

  const [input, setInput] = useState("");
  const [editing, setEditing] = useState(false);

  const remove = useMutationRequest<
    WatchlistType,
    RemoveSymbolFromWatchlistParams
  >((params) => removeSymbolFromWatchlist(params), {
    onSuccess: () => {
      toastSuccess("Removed from watchlist");
    },
    onError: () => {
      toastError("Failed to remove from watchlist");
    },
    onSettled: () => {
      refetch();
    },
  });

  const add = useMutationRequest<WatchlistType, AddSymbolToWatchlistParams>(
    (params) => addSymbolToWatchlist(params),
    {
      onSuccess: () => {
        toastSuccess("Symbol added to watchlist");
      },
      onError: () => {
        toastError("Symbol not added to watchlist");
      },
      onSettled: () => {
        refetch();
      },
    },
  );

  const onAddClicked = () => {
    // save the watchlist
    add.mutate({
      accountID: account?.id || "",
      name: PRIMARY_WATCHLIST_NAME,
      symbol: input.toUpperCase(),
    });
    setInput("");
  };

  // if user hits the Enter key, we should attempt to save the watchlist
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      onAddClicked();
    }
  };

  return (
    <Card
      title="Watchlist"
      tools={[
        <Button
          variant="ghost"
          key="watchlist-button"
          onClick={() => setEditing(!editing)}
          className="mb-4 w-fit"
        >
          {editing ? "Done" : "Edit"}
        </Button>,
      ]}
    >
      <div className="space-y-2 overflow-x-scroll">
        {editing && (
          <div className="mb-8 flex w-full items-center justify-between gap-2">
            <Input
              value={input}
              onChange={(event) => setInput(event.currentTarget.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter a symbol or crypto pair"
              className="w-full grow"
            />
            <Button variant="ghost" onClick={onAddClicked}>
              <Icon name="PlusCircle" />
            </Button>
          </div>
        )}
        {!watchlistAssets.length && (
          <Text className="text-center text-gray-600">
            No assets in watchlist
          </Text>
        )}
        <table className="mt-1 w-full whitespace-nowrap text-left">
          <tbody>
            {watchlistAssets.map((asset) => {
              const quote = quotes?.find(
                ({ symbol }) => symbol === asset.symbol,
              );
              const change = getPercentageChange(
                Number(quote?.last_day_closing),
                Number(quote?.last),
              );

              return (
                <tr
                  key={asset.symbol}
                  className="flex items-center justify-between gap-2"
                >
                  <td className="py-0.5 font-semibold">
                    <SymbolLink symbol={asset.symbol} />
                  </td>
                  <td
                    className={cn(
                      "text-right",
                      change >= 0 ? "text-emerald-500" : "text-rose-500",
                    )}
                  >
                    {change >= 0 ? "+" : ""}
                    {change.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    %
                  </td>
                  <td className="grow text-right">
                    $
                    {Number(quote?.last || 0).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 4,
                    })}
                  </td>
                  {editing && (
                    <td className="">
                      <Button
                        variant="ghost"
                        onClick={() =>
                          remove.mutate({
                            name: PRIMARY_WATCHLIST_NAME,
                            accountID: account?.id || "",
                            symbols: watchlistAssets
                              .filter((a) => a.symbol !== asset.symbol)
                              .map(({ symbol }) => symbol),
                          })
                        }
                      >
                        <Icon name="Trash" size="sm" className="text-red-400" />
                      </Button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default Watchlist;
